import type { ReactNode } from 'react';
import { useApp, type AgentRow } from '../state';
import { characterSVG, type Look } from './character';

// Identity. People are round with initials; agents are square in their own color, always tagged as an AI teammate
// wherever they speak. Since 2026-10-09 an agent wears the face the 3HUE Enterprise Hub gives it, at card and chat-header
// size, so a teammate looks the same in every system; in bylines and lists the square carries the sigil, and the AI tag
// still names every draft, so nobody mistakes one for a person's.

/** The five sigils, drawn as stroke SVG so they scale and take the avatar's white. */
export function Sigil({ name }: { name: string }) {
  switch (name) {
    case 'lens': return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="6.5" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3" /><circle cx="12" cy="12" r="1.6" fill="#fff" stroke="none" /></svg>;
    case 'sun': return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3.5" /><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1" /></svg>;
    case 'grid': return <svg viewBox="0 0 24 24" aria-hidden="true">{[7, 12, 17].flatMap((y) => [7, 12, 17].map((x) => <circle key={`${x}${y}`} cx={x} cy={y} r="1.8" fill="#fff" stroke="none" />))}</svg>;
    case 'compass': return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" /><path d="M15.5 8.5l-2 5-5 2 2-5z" fill="#fff" stroke="none" /></svg>;
    case 'hex': return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7.8 4.5v9L12 21l-7.8-4.5v-9z" /><circle cx="12" cy="12" r="2" fill="#fff" stroke="none" /></svg>;
    default: return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="7" /></svg>;
  }
}

export const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';

/** A stable color for a person from their name: five navy and cyan tones, never an agent's color. */
const HUMAN = ['#1e293b', '#2f86b3', '#334155', '#1d6a93', '#0f172a'];
export const humanColor = (name: string) => HUMAN[[...name].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7) % HUMAN.length];

type Size = 's' | 'm' | 'l' | 'xl';
const sz = (s: Size) => (s === 'm' ? '' : ` ${s}`);

/** What a face needs: the Hub's look and both tile colors. A row without them (a deliverable's byline) keeps the sigil. */
export type FaceRow = Pick<AgentRow, 'code' | 'name' | 'role_title' | 'color'> & { color_hi?: string | null; look?: Record<string, unknown> | null; hub_status?: string | null };
export const hasFace = (a: Partial<FaceRow> | null | undefined): a is FaceRow =>
  !!a && typeof a.code === 'string' && typeof a.name === 'string' && typeof a.role_title === 'string' && typeof a.color === 'string' && !!a.color_hi && !!a.look && typeof (a.look as { hair?: unknown }).hair === 'string';

const faces = new Map<string, string>();
/** The face as SVG markup, drawn once per look and kept; the Hub's head-and-shoulders crop. */
export function faceMarkup(a: FaceRow): string {
  const key = `${a.code}|${a.color}|${a.color_hi}|${a.hub_status ?? ''}|${JSON.stringify(a.look)}`;
  let svg = faces.get(key);
  if (svg === undefined) {
    const look = { key: a.code, name: a.name, role: a.role_title, hi: a.color_hi as string, lo: a.color, status: a.hub_status === 'onboarding' ? 'onb' : 'on', ...(a.look as object) } as Look;
    try { svg = characterSVG(look, `f_${a.code}_`, { thumb: true }); } catch { svg = ''; }
    faces.set(key, svg);
  }
  return svg;
}

export function AgentFace({ agent, size = 'xl', title, off }: { agent: FaceRow; size?: Size; title?: string; off?: boolean }) {
  const svg = faceMarkup(agent);
  if (!svg) return null;
  return <span className={`av agent face${sz(size)}${off ? ' off' : ''}`} style={{ background: agent.color }} role="img" aria-label={title ?? `${agent.name}, AI teammate`} dangerouslySetInnerHTML={{ __html: svg }} />;
}

export function AgentAvatar({ agent, size = 'm', title }: { agent: Pick<AgentRow, 'name' | 'color' | 'sigil' | 'is_enabled'> & Partial<FaceRow>; size?: Size; title?: string }) {
  if ((size === 'l' || size === 'xl') && hasFace(agent)) return <AgentFace agent={agent} size={size} title={title} off={!agent.is_enabled} />;
  return <span className={`av agent${sz(size)}${agent.is_enabled ? '' : ' off'}`} style={{ background: agent.color }} role="img" aria-label={title ?? `${agent.name}, AI teammate`}><Sigil name={agent.sigil} /></span>;
}
export function HumanAvatar({ name, size = 'm', color }: { name: string; size?: Size; color?: string }) {
  return <span className={`av human${sz(size)}`} style={{ background: color ?? humanColor(name) }} role="img" aria-label={name}>{initials(name)}</span>;
}

/** Avatar for whoever a row names: an agent by code, else a person by name. */
export function Avatar({ agentCode, name, size = 'm' }: { agentCode?: string | null; name?: string | null; size?: Size }) {
  const { agentsByCode } = useApp();
  const a = agentCode ? agentsByCode.get(agentCode) : undefined;
  if (a) return <AgentAvatar agent={a} size={size} />;
  return <HumanAvatar name={name || '—'} size={size} />;
}

export const AiTag = ({ children }: { children?: ReactNode }) => { const { disclosure } = useApp(); return <span className="tag ai">{children ?? disclosure}</span>; };

/** "Marlowe drafted this at 9:12 · v2 · $0.31" with the avatar and the AI tag. */
export function Byline({ agentCode, name, verb = 'drafted this', when, extra }: { agentCode?: string | null; name?: string | null; verb?: string; when?: ReactNode; extra?: ReactNode }) {
  const { agentsByCode } = useApp();
  const a = agentCode ? agentsByCode.get(agentCode) : undefined;
  const who = a?.name ?? name;
  if (!who) return null;
  return (
    <span className="byline">
      <Avatar agentCode={agentCode} name={name} size="s" />
      <span><b>{who}</b> {verb}{when ? <> at {when}</> : null}{extra ? <> · {extra}</> : null}</span>
      {a && <AiTag />}
    </span>
  );
}

export const STATUS_TAG: Record<string, string> = { working: 'ai', queued: 'ai', 'awaiting review': 'review', 'done today': 'ok', 'needs help': 'bad', idle: '', off: '' };
export const AgentStatus = ({ status }: { status: string }) => <span className={`tag ${STATUS_TAG[status] ?? ''}`}>{status}</span>;
